<template>
    <div class="row" v-if="local_block !== undefined && lang != undefined">
        <!-- TYPE -->
        <div class="col-md-6">
            <div class="form-group mb-15 mb-sm-20 mb-md-25">
                <label class="d-block text-black fw-semibold mb-10">
                    {{ $t('feature.contents.$blocks.type') }}*
                </label>
                <div>
                    <select class="form-select shadow-none text-black fs-md-15" v-model="local_block.type"
                        :class="[{ 'is-invalid': submitted && errors?.value }]" required :disabled="update"
                        @change="resetBlock()">
                        <option v-for="block_type in block_types" :value="block_type" :key="block_type">
                            {{ $t(`feature.contents.$blocks.$type.${block_type}`) }}</option>
                    </select>
                    <!-- ERRORS -->
                    <div class="form-messages mt-2 px-1" v-if="errors?.type ?? false">
                        <p v-for="error in errors.type" :key="error" class="small text-danger lh-1 m-0 mt-1">{{
                            error }}</p>
                    </div>
                </div>
            </div>
        </div>
        <div class="col-12"></div>
        <!-- TITLE -->
        <div class="col-md-6">
            <div class="form-group mb-15 mb-sm-20 mb-md-25">
                <label class="d-block text-black fw-semibold mb-10">
                    {{ $t('form.subtitle.label') }}*
                </label>
                <input v-if="local_block.subtitle?.[lang] !== undefined" v-model="local_block.subtitle![lang]"
                    class="form-control shadow-none text-black"
                    :class="[{ 'is-invalid': submitted && errors?.subtitle }]" />
                <!-- ERRORS -->
                <div class="form-messages mt-2 px-1" v-if="errors?.subtitle ?? false">
                    <p v-for="error in errors.subtitle" :key="error" class="small text-danger lh-1 m-0 mt-1">
                        {{ error }}</p>
                </div>
            </div>
        </div>
        <!-- VALUE -->
        <!-- TEXT -->
        <div class="col-12" v-if="local_block.type === 'text'">
            <div class="form-group mb-15 mb-sm-20 mb-md-25">
                <label class="d-block text-black fw-semibold mb-10">
                    {{ $t('form.value.label') }}*
                </label>
                <textarea v-if="local_block.value?.[lang] !== undefined" v-model="local_block.value![lang]"
                    class="form-control shadow-none text-black" rows="6"
                    :class="[{ 'is-invalid': submitted && errors?.value }]" required></textarea>
                <!-- ERRORS -->
                <div class="form-messages mt-2 px-1" v-if="errors?.value ?? false">
                    <p v-for="error in errors.value" :key="error" class="small text-danger lh-1 m-0 mt-1">
                        {{ error }}</p>
                </div>
            </div>
        </div>
        <!-- IMAGE -->
        <div class="col-12" v-if="local_block.type === 'image'">
            <div class="mb-15 mb-md-20 mb-lg-25">
                <label class="d-block text-black fw-semibold mb-10" :for="block.type">
                    {{ $t('form.value.label') }}*
                </label>
                <input class="form-control fs-15 text-black shadow-none" type="file" :id="block.type"
                    :required="!update" @change="event => fileUploaded(event)" accept="image/png, image/jpeg">
                <!-- ERRORS -->
                <div class="form-messages mt-2 px-1" v-if="errors?.value ?? false">
                    <p v-for="error in errors.value" :key="error" class="small text-danger lh-1 m-0 mt-1">
                        {{ error }}</p>
                </div>
            </div>
        </div>
        <!-- VIDEO -->
        <div class="col-12" v-if="local_block.type === 'video'">
            <div class="form-group mb-15 mb-sm-20 mb-md-25">
                <label class="d-block text-black fw-semibold mb-10">
                    {{ $t('form.url.label') }}*
                </label>
                <input v-model="local_block.value![lang]" type="url" class="form-control shadow-none text-black"
                    rows="6" :class="[{ 'is-invalid': submitted && errors?.value }]" required />
                <!-- ERRORS -->
                <div class="form-messages mt-2 px-1" v-if="errors?.value ?? false">
                    <p v-for="error in errors.value" :key="error" class="small text-danger lh-1 m-0 mt-1">
                        {{ error }}</p>
                </div>
            </div>
        </div>
        <!-- HTML -->
        <div class="col-12" v-if="local_block.type === 'html'">
            <div class="form-group mb-15 mb-sm-20 mb-md-25">
                <div class="d-flex justify-content-between">
                    <label class="d-block text-black fw-semibold mb-10">
                        {{ $t('form.value.label') }}*
                    </label>
                    <div class="icon-link transition mb-10 cursor-pointer text-uppercase" @click="toggleRawEdit">
                        <i class="ph ph-code" v-if="!raw_edit"></i>
                        {{ raw_edit ? $t('feature.contents.$blocks.editor') : $t('feature.contents.$blocks.raw') }}
                    </div>
                </div>
                <!-- RAW EDIT -->
                <textarea v-if="raw_edit" v-model="local_block.value![lang]" class="form-control shadow-none text-black"
                    rows="8" :class="[{ 'is-invalid': submitted && errors?.value }]" required
                    :key="`${block.id}-${lang}-${changes}-input`" v-on:paste="(e) => minify_value(e)"></textarea>
                <div class="mb-0 quill-editor" v-else>
                    <QuillEditor theme="snow" :toolbar="toolbar" v-model:content="local_block.value![lang]"
                        contentType="html" output="delta" :key="`${block.id}-${lang}-${changes}-editor`" />
                </div>
                <!-- ERRORS -->
                <div class="form-messages mt-2 px-1" v-if="errors?.value ?? false">
                    <p v-for="error in errors.value" :key="error" class="small text-danger lh-1 m-0 mt-1">
                        {{ error }}</p>
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts">
import QUILL_TOOLBAR from '@/utils/const/quill_config';
import getBase64 from '@/utils/methods/get_base_64';
import { minify } from '@/utils/methods/minify';
import { ContentBlock, ContentBlockType } from '@/utils/models/content.model';
import { PropType } from 'vue';
import { defineComponent } from 'vue';

export default defineComponent({
    props: {
        block: {
            type: Object as PropType<ContentBlock>,
            required: true,
        },
        submitted: {
            type: Boolean,
            requred: true,
        },
        errors: {
            type: Object as PropType<any>,
        },
        update: {
            type: Boolean,
            required: true,
        },
        lang: {
            type: String,
            default: process.env.VUE_APP_LANG as string,
        }
    },
    watch: {
        lang: function (locale) {
            this.normalize_block();
        },
        block: function (value) {
            this.local_block = value;
            this.normalize_block();
            this.changes++;
        }
    },
    data() {
        return {
            local_block: this.block,
            block_types: ['text', 'image', 'video', 'html'] as ContentBlockType[],
            raw_edit: false,
            changes: 0,
            toolbar: QUILL_TOOLBAR
        }
    },
    mounted() {
        this.normalize_block();
    },
    methods: {
        async fileUploaded($event) {
            this.local_block.value![this.lang] = await getBase64($event.target.files[0]);
        },
        resetBlock() {
            this.local_block.subtitle = {}
            this.local_block.value = {}
            this.normalize_block();
            this.raw_edit = false;
            this.changes++;
        },
        toggleRawEdit() {
            try {
                this.local_block.value![this.lang] = minify(this.local_block.value![this.lang!] ?? '');
            } catch (error) { }
            this.raw_edit = !this.raw_edit;
        },
        normalize_block() {
            this.local_block.subtitle ??= {};
            this.local_block.value ??= {};
            if (!this.local_block.subtitle?.hasOwnProperty(this.lang)) {
                this.local_block.subtitle[this.lang] = "";
            }
            if (!this.local_block.value?.hasOwnProperty(this.lang)) {
                this.local_block.value[this.lang] = "";
            }
        },
        minify_value(event: ClipboardEvent) {
            try {
                setTimeout(() => {
                    let text = (event.target as HTMLTextAreaElement).value;
                    (event.target as HTMLTextAreaElement).value = minify(text);
                    this.local_block.value![this.lang] = minify(text);
                }, 0)
            } catch (error) { }
        }
    },
    name: "BlockForm",
});
</script>